'use client';

import React, { useEffect, useState, useRef, useContext } from 'react';
import { CaretDownIcon, CheckIcon, UserIcon } from '@phosphor-icons/react';
import { Spinner } from '@heroui/spinner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { FetchWithVersionCheck } from '../shared/functions';
import { AuthContext } from '@/app/contexts';
import { ThreatReportKey } from '@/shared/types';
import { Button } from '@heroui/button';

/**
 * Threat Report Component Styles
 * 
 * Styles are kept in this component file rather than globals.css to keep the component
 * self-contained and maintainable. All threat-report specific styling is defined here
 * to make it easier to modify and understand the component's visual design in one place.
 */
const threatReportStyles = `
  .threat-report,
  .threat-report p { 
    font-family: Jost;
    font-weight: 400;
    font-size: 18px;
  }

  .threat-report h1,
  .threat-report h2,
  .threat-report h3,
  .threat-report h4,
  .threat-report h5,
  .threat-report h6 {
    font-weight: 400;
    text-transform: uppercase;
    text-align: center;
  }

  .threat-report .clarion-logo { 
    background-image: url('/clarion-project-logo@2x.png'); 
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    width: 228px;
    height: 96px;
    image-rendering: -webkit-optimize-contrast;
    backface-visibility: hidden;
    transform: translateZ(0);
  }
  .threat-report .clarion-logo div { display: none; }

  .threat-report .clarion-intelligence-network { 
    background-image: url('/clarion-intelligence-network@2x.png'); 
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    width: 178px;
    height: 96px;
    image-rendering: -webkit-optimize-contrast;
    backface-visibility: hidden;
    transform: translateZ(0);
  }
  .threat-report .clarion-intelligence-network div { display: none; }

  .threat-report .clarion-seal { 
    background-image: url('/clarion-seal@2x.png'); 
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    width: 410px;
    height: 365px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    margin-right: auto;
    image-rendering: -webkit-optimize-contrast;
    backface-visibility: hidden;
    transform: translateZ(0);
  }

  .threat-report .clarion-seal div { display: none; }

  .threat-report-table,
  .threat-report-table div {
    /* border: 1px solid grey; */
  }

  .threat-report-heading {
    /* border: 1px solid grey; */
  }

  .threat-report .threat-report-assessment,
  .threat-report .threat-report-assessment p {
    font-size: 18px;
    line-height: 1.5;
    text-align: left;
  }

  .threat-report .threat-report-assessment h1,
  .threat-report .threat-report-assessment h2,
  .threat-report .threat-report-assessment h3,
  .threat-report .threat-report-assessment h4,
  .threat-report .threat-report-assessment h5,
  .threat-report .threat-report-assessment h6 {
    text-align: left;
    font-weight: 700;
    text-transform: none;
  }

  .threat-report .threat-report-assessment ul,
  .threat-report .threat-report-assessment ol {
    margin-left: 20px;
  }

  .threat-report .threat-report-assessment li {
    margin-left: 20px;
    margin-bottom: 0;
  }

  .threat-report .threat-report-assessment em {
    font-style: normal;
    font-weight: 700;
  }

  .threat-report .threat-report-assessment sup {
    font-size: 12px;
    vertical-align: super;
  }

  .threat-report .threat-report-assessment sup a {
    text-decoration: none;
    color: inherit;
  }

  .actionable-threats {
    padding-left: 0px;
  }

  .actionable-threats ol {
    list-style-position: inside;
    margin-left: 0;
    margin-bottom: 0;
  }

  .actionable-threats li {
    list-style-type: none;
    margin-left: 0;
    margin-bottom: 0;
  }

  .actionable-threats time {
    font-weight: 700 !important;
  }
`;
export interface ContactInfo {
  crypto?: string;
  alias?: string;
  platform?: string;
  idAccount?: string;
  url?: string;
  dateCreated?: string;
  publicPrivate?: string;
  active?: string;
}
export interface KeyAssociate {
  alias?: string;
  platform?: string;
  idAccount?: string;
  url?: string;
  dateCreated?: string;
  publicPrivate?: string;
  active?: string;
}
export interface ActionableThreat {
  date?: string;
  evidenceSummary?: string;
  link?: string;
}
export interface SocialMediaPost {
  date?: string;
  activityId: string;
  notes?: string;
  media?: Array<{
    type: 'image' | 'video';
    url: string;
  }>;
}

/**
 * Single processing message from AI agent
 * Messages are added incrementally as they're generated and displayed in real-time
 */
export interface ProcessingMessage {
  id: string;
  type: 'agent_response' | 'tool_execution' | 'progress_update';
  content: string;
  timestamp: string;
  metadata?: Record<string, any>;
}
export interface ThreatAssessmentInfo {
  classification?: string;
  groupAffiliation?: string;
  ideology?: string;
}
export interface ProfileInfo {
  realName?: string;
  alias?: string;
  dob?: string;
  classification?: string;
  group?: string;
  ideology?: string;
  address?: string;
  cityState?: string;
  email?: string;
  phone?: string;
  profilePhoto?: string;
}
export interface ThreatReportProps {
  threatReportKey?: ThreatReportKey | undefined;
  userId?: string;
  tenant?: string | null;
  caseId?: string;
  reportNumber?: string; // EVIPAK#XYZ
  profile?: ProfileInfo;
  contactInfo?: ContactInfo[];
  keyAssociates?: KeyAssociate[];
  actionableThreats?: ActionableThreat[];
  socialMediaPosts?: SocialMediaPost[];
  threatAssessment?: ThreatAssessmentInfo;
  narrativeThreatAssessment?: any;
  setIsThreatReportProcessing?: (isProcessing: boolean) => void;
  clearThreatReportKey: () => void;
}

/**
 * Infer media type from URL extension
 */
const inferMediaType = (url: string): 'image' | 'video' => {
  const urlLower = url.toLowerCase();
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.flv', '.wmv', '.m4v'];
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp', '.tiff', '.ico'];
  for (const ext of videoExtensions) {
    if (urlLower.includes(ext)) {
      return 'video';
    }
  }
  for (const ext of imageExtensions) {
    if (urlLower.includes(ext)) {
      return 'image';
    }
  }

  // Default to image if unknown
  return 'image';
};

/**
 * Extract value from a key-value pair line
 * Handles formats like "- Key: Value" or "- Key: Value"
 */
const extractKeyValue = (line: string): {
  key: string;
  value: string;
} | null => {
  const match = line.match(/^[\s\t]*-[\s\t]*(.+?):[\s\t]*(.+)$/);
  if (match) {
    return {
      key: match[1].trim(),
      value: match[2].trim()
    };
  }
  return null;
};

/**
 * Convert S3 URL to proxy URL for display
 */
const convertS3UrlToProxyUrl = (url: string): string => {
  if (url.startsWith('s3://')) {
    return `/api/proxy/${encodeURIComponent(url)}`;
  }
  return url; // Return as-is for http/https URLs
};
const POLLING_TIME_LIMIT = 1800; // 30 minutes
const POLLING_INTERVAL = 3000; // 3 seconds
const POLLING_RAMPUP = 1000; // 1 seconds

/**
 * ThreatReport Component
 * 
 * Template component for displaying threat reports matching Google Doc formatting.
 * Uses Jost font with Inter fallback, flexbox-based layouts for future responsiveness.
 */
const ThreatReport: React.FC<ThreatReportProps> = ({
  threatReportKey,
  userId,
  tenant,
  caseId,
  reportNumber,
  profile,
  contactInfo,
  keyAssociates,
  actionableThreats,
  socialMediaPosts,
  threatAssessment,
  narrativeThreatAssessment,
  setIsThreatReportProcessing,
  clearThreatReportKey
}) => {
  const [finalNarrativeThreatAssessmentMd, setFinalNarrativeThreatAssessmentMd] = useState<string | null>(null);
  const processingMessagesRef = useRef<HTMLDivElement>(null);
  const pollForThreatReportTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  /** After revert we clear the parent key; skip one keyless effect cycle to avoid a second loadThreatReportFromTable. */
  const suppressNextKeylessLoadEffectRef = useRef(false);

  // Local state to override isProcessing prop for testing the Complete button
  const [localIsProcessing, setLocalIsProcessing] = useState<boolean>(true);
  const [pollForThreatReport, setPollForThreatReport] = useState<number>(0);
  const [processingMessages, setProcessingMessages] = useState<ProcessingMessage[]>([]);
  const [startCheckingForFinalResult, setStartCheckingForFinalResult] = useState<boolean>(false);
  const [threatReportJson, setThreatReportJson] = useState<any>(null);
  const [threatReportStatus, setThreatReportStatus] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [threatReportKeyLocal, setThreatReportKeyLocal] = useState<ThreatReportKey | undefined>(threatReportKey);

  // Final result vars that repolace props when completed
  const [finalReportNumber, setFinalReportNumber] = useState<string | undefined>(reportNumber);
  const [finalProfile, setFinalProfile] = useState<ProfileInfo | undefined>(profile);
  const [finalContactInfo, setFinalContactInfo] = useState<ContactInfo[] | undefined>(contactInfo);
  const [finalKeyAssociates, setFinalKeyAssociates] = useState<KeyAssociate[] | undefined>(keyAssociates);
  const [finalActionableThreats, setFinalActionableThreats] = useState<ActionableThreat[] | undefined>(actionableThreats);
  const [finalSocialMediaPosts, setFinalSocialMediaPosts] = useState<SocialMediaPost[] | undefined>(socialMediaPosts);
  const [finalThreatAssessment, setFinalThreatAssessment] = useState<ThreatAssessmentInfo | undefined>(threatAssessment);
  const [finalNarrativeThreatAssessment, setFinalNarrativeThreatAssessment] = useState<any | null>(narrativeThreatAssessment);
  const {
    idToken,
    accessToken
  } = useContext(AuthContext);
  useEffect(() => {
    if (threatReportKey?.epochTimeSecs && threatReportKey?.epochTimeSecs > 0) {
      setThreatReportKeyLocal(threatReportKey);
      loadThreatReportFromTable();
    } else {
      if (suppressNextKeylessLoadEffectRef.current) {
        suppressNextKeylessLoadEffectRef.current = false;
        setThreatReportKeyLocal(undefined);
        if (pollForThreatReportTimeoutRef.current) {
          clearTimeout(pollForThreatReportTimeoutRef.current);
          pollForThreatReportTimeoutRef.current = null;
        }
        return;
      }
      setThreatReportKeyLocal(undefined);
      if (pollForThreatReportTimeoutRef.current) {
        clearTimeout(pollForThreatReportTimeoutRef.current);
        pollForThreatReportTimeoutRef.current = null;
      }
      setErrorText(null);
      setPollForThreatReport(0);
      setThreatReportJson(null);
      setThreatReportStatus(null);
      setLocalIsProcessing(true);
      setProcessingMessages([]);
      // Pass 0: state from setPollForThreatReport(0) is not visible until the next render, so the
      // polling counter must not use the stale closure value inside loadLastThreatReportFromTable.
      loadThreatReportFromTable(0);
    }
  }, [threatReportKey?.epochTimeSecs]);
  useEffect(() => {
    if (threatReportJson) {
      setFinalReportNumber(threatReportJson.reportNumber);
      setFinalProfile(threatReportJson.profile);
      setFinalContactInfo(threatReportJson.contactInfo);
      setFinalKeyAssociates(threatReportJson.keyAssociates);
      setFinalActionableThreats(threatReportJson.actionableThreats);
      setFinalSocialMediaPosts(threatReportJson.socialMediaPosts);
      setFinalThreatAssessment(threatReportJson.threatAssessment);
      setFinalNarrativeThreatAssessment(threatReportJson.narrativeThreatAssessment);
    } else {
      setFinalReportNumber(undefined);
      setFinalProfile(undefined);
      setFinalContactInfo(undefined);
      setFinalKeyAssociates(undefined);
      setFinalActionableThreats(undefined);
      setFinalSocialMediaPosts(undefined);
      setFinalThreatAssessment(undefined);
      setFinalNarrativeThreatAssessment(null);
    }
  }, [threatReportJson]);
  const fetchThreatReportFromTable = async (currentThreatReportKey?: ThreatReportKey): Promise<any | null> => {
    if (!caseId) {
      return null;
    }
    try {
      // if threatReportKey is provided, we are looking for that specific threat report
      const {
        chatKey,
        sessionId,
        epochTimeSecs,
        requestType
      } = currentThreatReportKey || {};
      const body = {
        action: 'getThreatReport',
        chatKey: chatKey,
        sessionId: sessionId,
        epochTimeSecs: epochTimeSecs,
        requestType: requestType
      };
      const options: any = {
        method: !!epochTimeSecs ? 'POST' : 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'x-tai-id-token': idToken ?? ''
        }
      };
      if (!!epochTimeSecs) {
        options.body = JSON.stringify(body);
      }
      const response = await FetchWithVersionCheck(`/api/protected/threatReport/${encodeURIComponent(caseId || '')}`, options);
      if (response.ok) {
        const data = await response.json();
        const completed = data.status === 'completed' || !data.status && !!data.threat_report_json && data.threat_report_json.trim() !== '';
        const error = data.status === 'error';
        setThreatReportStatus(data.status ? data.status : completed ? 'completed' : 'unknown');
        if ((completed || error) && data && data.epoch_time_secs) {
          if (!!data.threat_report_json) {
            try {
              const lastThreatReportJson = JSON.parse(data.threat_report_json);
              setThreatReportJson(lastThreatReportJson);
            } catch {
              setThreatReportJson(null);
            }
          } else {
            setThreatReportJson(null);
          }
        }
        // Return the row for any specific GET/POST hit with epoch_time_secs so loadThreatReportFromTable
        // can drive polling and processing UI (not only terminal completed/error states).
        if (data?.epoch_time_secs) {
          // Do not schedule polling here: loadThreatReportFromTable owns pollForThreatReportTimeoutRef
          // and enforces POLLING_TIME_LIMIT. A timer here was left running when load exited early at the limit.
          return data;
        }
        return null;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  };
  const deleteErrorThreatReportFromTable = async (currentThreatReportKey?: ThreatReportKey) => {
    const {
      chatKey,
      sessionId,
      epochTimeSecs,
      requestType
    } = currentThreatReportKey || {};
    const body = {
      action: 'deleteErrorThreatReport',
      chatKey: chatKey,
      sessionId: sessionId,
      epochTimeSecs: epochTimeSecs,
      requestType: requestType
    };
    const response = await FetchWithVersionCheck(`/api/protected/threatReport/${encodeURIComponent(caseId || '')}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'x-tai-id-token': idToken ?? ''
      },
      body: JSON.stringify(body)
    });
    if (response.ok) {
      return true;
    } else {
      return false;
    }
  };
  const revertToLastThreatReport = async (currentThreatReportKey?: ThreatReportKey) => {
    setThreatReportStatus(null);
    setThreatReportJson(null);
    setErrorText(null);
    setPollForThreatReport(0);
    setProcessingMessages([]);
    setLocalIsProcessing(true);
    await deleteErrorThreatReportFromTable(currentThreatReportKey);
    setThreatReportKeyLocal(undefined);
    try {
      await loadThreatReportFromTable(0, {
        useLatestFromTable: true
      });
    } finally {
      suppressNextKeylessLoadEffectRef.current = true;
      clearThreatReportKey();
    }
  };
  const loadThreatReportFromTable = async (pollElapsedOverride?: number, options?: {
    useLatestFromTable?: boolean;
  }) => {
    setThreatReportStatus(null);
    setErrorText(null);
    try {
      const pollElapsed = pollElapsedOverride !== undefined ? pollElapsedOverride : pollForThreatReport;
      const fetchKey = options?.useLatestFromTable ? undefined : threatReportKey;
      const lastThreatReport = await fetchThreatReportFromTable(fetchKey);
      if (!lastThreatReport) {
        if (pollForThreatReportTimeoutRef.current) {
          clearTimeout(pollForThreatReportTimeoutRef.current);
          pollForThreatReportTimeoutRef.current = null;
        }
        setPollForThreatReport(0);
        setProcessingMessages([]);
        setThreatReportStatus('not_found');
        setErrorText('Could not load any threat report.');
        setLocalIsProcessing(false);
        if (setIsThreatReportProcessing) {
          setIsThreatReportProcessing(false);
        }
        return;
      }

      // Check if we have a completed threat report (with threat_report_json)
      const oldStyleCompleted = !!lastThreatReport?.threat_report_json && lastThreatReport?.threat_report_json.trim() !== '';
      const completed = lastThreatReport?.status === 'completed';
      const reallyCompleted = completed || !lastThreatReport?.status && oldStyleCompleted;
      if (reallyCompleted && lastThreatReport?.threat_report_json) {
        setThreatReportStatus(!!lastThreatReport?.status ? lastThreatReport.status : oldStyleCompleted ? 'completed' : 'unknown');
        // Completed report exists - load it and stop processing
        setStartCheckingForFinalResult(false);
        if (pollForThreatReportTimeoutRef.current) {
          clearTimeout(pollForThreatReportTimeoutRef.current);
          pollForThreatReportTimeoutRef.current = null;
        }
        setPollForThreatReport(0);
        setProcessingMessages([]);
        setLocalIsProcessing(false);
        setThreatReportJson(JSON.parse(lastThreatReport.threat_report_json));
        if (setIsThreatReportProcessing) {
          setIsThreatReportProcessing(false);
        }
        return;
      } else if (lastThreatReport?.status === 'error') {
        const lastThreatReportKey = {
          chatKey: lastThreatReport.chat_key,
          sessionId: lastThreatReport.session_id,
          epochTimeSecs: lastThreatReport.epoch_time_secs,
          requestType: lastThreatReport.request_type
        };
        setThreatReportKeyLocal(lastThreatReportKey);
        setThreatReportStatus('error');
        let resolvedError: string | null = null;
        try {
          const answer = JSON.parse(lastThreatReport.answer);
          if (typeof answer?.output === 'string' && answer.output.trim() !== '') {
            resolvedError = answer.output;
          }
        } catch {
          // use fallbacks below
        }
        if (!resolvedError && typeof lastThreatReport.output === 'string' && lastThreatReport.output.trim() !== '') {
          resolvedError = lastThreatReport.output;
        }
        setErrorText(resolvedError ?? 'There was an error generating the last threat report.');
        setLocalIsProcessing(false);
        if (setIsThreatReportProcessing) {
          setIsThreatReportProcessing(false);
        }
        return;
      }
      if (lastThreatReport?.answer) {
        let answer = null;
        try {
          answer = JSON.parse(lastThreatReport.answer);
        } catch (error) {
          // try again
        }
        if (answer) {
          const status = 'status' in answer ? answer.status : 'unknown';
          const isInitializing = status === 'initializing';
          const isInProgress = status === 'inprogress';
          const isCompleted = status === 'completed';
          const hasError = status === 'error';
          const isPrelimary = 'is_preliminary' in answer && answer.is_preliminary === true;
          const containsMessages = !!answer && 'messages' in answer && answer.messages.length > 0 ? true : false;
          const isPreliminaryResult = isInitializing || isInProgress || isPrelimary;
          // Do not treat empty messages as "final" while still initializing/in progress (avoids empty template flash).
          const isFinalResult = isPreliminaryResult ? false : isCompleted || containsMessages && !isPreliminaryResult;
          const newLocalIsProcessing = !hasError && !isFinalResult;
          setLocalIsProcessing(newLocalIsProcessing);
          setThreatReportStatus(status);
          if (!hasError) {
            if (!newLocalIsProcessing || startCheckingForFinalResult && isFinalResult) {
              setStartCheckingForFinalResult(false);
              if (pollForThreatReportTimeoutRef.current) {
                clearTimeout(pollForThreatReportTimeoutRef.current);
                pollForThreatReportTimeoutRef.current = null;
              }
              setPollForThreatReport(0);
              setProcessingMessages([]);
              setLocalIsProcessing(false);
              setThreatReportJson(JSON.parse(lastThreatReport?.threat_report_json || '{}'));
              if (setIsThreatReportProcessing) {
                setIsThreatReportProcessing(false);
              }
              return;
            } else if (!isFinalResult) {
              if (!startCheckingForFinalResult) {
                setStartCheckingForFinalResult(true);
              }
              const processingMessages = answer?.messages?.map((message: ProcessingMessage) => {
                return {
                  id: message.id,
                  content: message.content,
                  timestamp: message.timestamp,
                  type: message.type,
                  metadata: message.metadata
                };
              });
              setLocalIsProcessing(true);
              setProcessingMessages(processingMessages);
              if (pollElapsed < POLLING_TIME_LIMIT) {
                if (pollForThreatReportTimeoutRef.current) {
                  clearTimeout(pollForThreatReportTimeoutRef.current);
                  pollForThreatReportTimeoutRef.current = null;
                }
                pollForThreatReportTimeoutRef.current = setTimeout(() => {
                  setPollForThreatReport(prev => prev + POLLING_INTERVAL / 1000);
                }, POLLING_INTERVAL);
              } else {
                if (pollForThreatReportTimeoutRef.current) {
                  clearTimeout(pollForThreatReportTimeoutRef.current);
                  pollForThreatReportTimeoutRef.current = null;
                }
                setLocalIsProcessing(false);
                if (setIsThreatReportProcessing) {
                  setIsThreatReportProcessing(false);
                }
              }
              return;
            }
            if (pollElapsed < POLLING_TIME_LIMIT) {
              if (pollForThreatReportTimeoutRef.current) {
                clearTimeout(pollForThreatReportTimeoutRef.current);
                pollForThreatReportTimeoutRef.current = null;
              }
              pollForThreatReportTimeoutRef.current = setTimeout(() => {
                setPollForThreatReport(prev => prev + POLLING_INTERVAL / 1000);
              }, POLLING_INTERVAL);
            }
          } else {
            setThreatReportStatus('error');
            setErrorText(answer.output);
            const lastThreatReportKey = {
              chatKey: lastThreatReport.chat_key,
              sessionId: lastThreatReport.session_id,
              epochTimeSecs: lastThreatReport.epoch_time_secs,
              requestType: lastThreatReport.request_type
            };
            setThreatReportKeyLocal(lastThreatReportKey);
            setStartCheckingForFinalResult(false);
            if (pollForThreatReportTimeoutRef.current) {
              clearTimeout(pollForThreatReportTimeoutRef.current);
              pollForThreatReportTimeoutRef.current = null;
            }
            setPollForThreatReport(0);
            setProcessingMessages([]);
            if (setIsThreatReportProcessing) {
              setIsThreatReportProcessing(false);
            }
            return;
          }
        }
      } else {
        // No threat report data or old timestamp - stop processing after timeout
        if (pollElapsed >= POLLING_TIME_LIMIT) {
          if (pollForThreatReportTimeoutRef.current) {
            clearTimeout(pollForThreatReportTimeoutRef.current);
            pollForThreatReportTimeoutRef.current = null;
          }
          setLocalIsProcessing(false);
          if (setIsThreatReportProcessing) {
            setIsThreatReportProcessing(false);
          }
        } else if (pollElapsed < POLLING_TIME_LIMIT) {
          if (pollForThreatReportTimeoutRef.current) {
            clearTimeout(pollForThreatReportTimeoutRef.current);
            pollForThreatReportTimeoutRef.current = null;
          }
          const delay = pollElapsed < POLLING_INTERVAL ? POLLING_RAMPUP : POLLING_INTERVAL;
          const incrementSecs = (pollElapsed < POLLING_INTERVAL ? POLLING_RAMPUP : POLLING_INTERVAL) / 1000;
          pollForThreatReportTimeoutRef.current = setTimeout(() => {
            setPollForThreatReport(prev => prev + incrementSecs);
          }, delay);
        }
      }
    } catch (error) {
      if (pollForThreatReportTimeoutRef.current) {
        clearTimeout(pollForThreatReportTimeoutRef.current);
        pollForThreatReportTimeoutRef.current = null;
      }
      setPollForThreatReport(0);
      setProcessingMessages([]);
      setErrorText('Could not load any threat report.');
      setThreatReportStatus('not_found');
      setLocalIsProcessing(false);
      if (setIsThreatReportProcessing) {
        setIsThreatReportProcessing(false);
      }
    }
  };
  useEffect(() => {
    if (pollForThreatReport > 0) {
      loadThreatReportFromTable();
    }
  }, [pollForThreatReport]);
  useEffect(() => {
    if (localIsProcessing && processingMessagesRef.current && !!processingMessages && processingMessages.length > 0) {
      processingMessagesRef.current.scrollTop = processingMessagesRef.current.scrollHeight - 120;
    }
  }, [processingMessages, localIsProcessing]);
  useEffect(() => {
    if (finalNarrativeThreatAssessment) {
      // if you change this, make sure to keep the \n\n or put everything in a template string
      let md = `## Narrative Threat Assessment\n\n` + `### Threat Level\n\n` + `- ${finalNarrativeThreatAssessment.threatLevel}\n\n` + `### Executive Summary\n\n` + `- ${finalNarrativeThreatAssessment.executiveSummary}\n\n` + `### Content Chronology and Analysis\n\n` + `${finalNarrativeThreatAssessment.contentChronology.split('\n\n').map((point: string) => `- ${point}`).join('\n\n')}` + `\n\n` + `### Key Concerning Elements\n\n` + `${finalNarrativeThreatAssessment.keyConcerningElements}\n\n` + `### Recommended Actions\n\n` + `${finalNarrativeThreatAssessment.recommendedActions}\n\n`;
      if (finalNarrativeThreatAssessment.conclusion && finalNarrativeThreatAssessment.conclusion.trim() !== '') {
        md += `### Conclusion\n\n` + `- ${finalNarrativeThreatAssessment.conclusion}\n\n`;
      }
      setFinalNarrativeThreatAssessmentMd(md);
    } else {
      setFinalNarrativeThreatAssessmentMd(null);
    }
  }, [finalNarrativeThreatAssessment, caseId]);

  // [REPLACE COLOR] - Classification box color (yellow/gold)
  const classificationBoxColor = '#FED766';
  // [REPLACE COLOR] - Group affiliation box color (light blue)
  const groupBoxColor = '#A8DADC';

  // Preload critical images for faster loading
  useEffect(() => {
    const preloadImages = ['/clarion-project-logo@2x.png', '/clarion-intelligence-network@2x.png', '/clarion-seal@2x.png'];
    preloadImages.forEach(src => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      link.fetchPriority = 'high';
      document.head.appendChild(link);
    });

    // Cleanup function to remove preload links when component unmounts
    return () => {
      preloadImages.forEach(src => {
        const existingLink = document.querySelector(`link[href="${src}"]`);
        if (existingLink) {
          document.head.removeChild(existingLink);
        }
      });
    };
  }, []);
  if (threatReportStatus === 'error' || threatReportStatus === 'not_found') {
    return <div className="h-full bg-[#0F1D32] overflow-auto px-[30px] py-[30px] text-white flex flex-col items-center justify-center text-center">
        {errorText && errorText.trim() !== '' ? <h1 className="text-center">{errorText}</h1> : <h1 className="text-center">There was an error generating the last threat report.</h1>}
        {threatReportStatus === 'error' && <h2 className="text-center">Please generate a new threat report to replace this one.</h2>}
        {threatReportKeyLocal && threatReportStatus === 'error' && <div className="cursor-pointer text-center">
            <Button className="text-center" onPress={() => revertToLastThreatReport(threatReportKeyLocal)}>
              Click here to remove this threat report and revert to the last successfully generated threat report.
            </Button>
          </div>}
      </div>;
  }
  return <>
      <style dangerouslySetInnerHTML={{
      __html: threatReportStyles
    }} />
      <div id="threat-report-docx" className="h-full bg-[#0F1D32] overflow-auto px-[30px] py-[30px] threat-report">
      {/* AI Processing Section */}
      {localIsProcessing && <div className="w-full max-w-full bg-[#0A1520] mb-4 rounded-lg">
          <div className="flex flex-row h-[calc(100vh-280px)] max-h-[calc(100vh-280px)] px-[20px] py-6">
            <div ref={processingMessagesRef} className="flex flex-col gap-0 text-white font-light text-[14px] leading-snug m-0 w-full overflow-y-auto custom-scrollbar2" aria-live="polite" aria-busy={localIsProcessing} role="status">
              {processingMessages && processingMessages.length > 0 && processingMessages.map((message: ProcessingMessage, index: number) => <div key={message.id} className="box-border w-full h-fit flex flex-row gap-0 items-center text-white font-light text-[14px] leading-snug m-0 min-h-fit">
                  <div className="min-w-[31px] flex flex-row items-center justify-start p-0">
                    {index === processingMessages.length - 1 ? <div className="relative z-1 flex flex-row gap-2 items-start justify-start w-full min-h-[32px]">
                        <div className="w-[27px] h-[27px] p-[3px] box-border flex items-center justify-center bg-[#0C1B2E] border-[1px] border-white border-solid rounded-full">
                          <Spinner variant="spinner" size="sm" className="[&_i]:bg-blue-300" />
                        </div>
                        <div className="box-border flex font-light w-full text-[14px] leading-snug min-h-[32px] mt-[4px] text-blue-300">
                          {message.content === "Analysis complete." ? "Analysis complete, the answer will be displayed in a moment." : message.content}
                        </div>
                      </div> : <div className="relative z-1 flex flex-row-reverse gap-2 items-start justify-start w-full min-h-[21px]">
                        <div className="relative z-1 box-border flex font-light w-full text-[14px] leading-snug pb-[10px] min-h-[32px] text-white
                          pl-[20px] -ml-[19px] mt-[2px] border-l-[1px] border-white border-solid">
                          {message.content}
                        </div>
                        <div className="relative z-2 box-border flex items-center justify-start w-[21px] h-[21px] max-h-[21px] min-h-[21px]
                            p-[3px] ml-[3px] bg-[#0C1B2E] border-[1px] border-white border-solid rounded-full">
                          <CheckIcon size={15} weight="bold" className="relative z-3 text-green-500" />
                        </div>
                      </div>}
                  </div>
                </div>)}
            </div>
          </div>
        </div>}
      {/* Template Wrapper - Only show when not processing */}
      {!localIsProcessing && <div className="w-full max-w-full min-h-full min-w-[1024px] bg-white border border-gray-300 mx-auto">
        <div className="flex flex-col p-8 font-jost text-black">
          
          {/* Header Section (Page 1) */}
          <header>

            <section aria-label="Organization logos" className="mb-8">
              <figure className="flex justify-center m-0">
                <img src="/clarion-tr-logos@2x.png" alt="Clarion Intelligence Network logos" className="scale-50" />
              </figure>
            </section>

            <>
              {/* Emblem/Seal */}
              <figure className="flex justify-center my-8">
                <img height={365} width={410} src="/clarion-seal@2x.png" alt="Clarion Intelligence Network seal" />
              </figure>

              {/* Classification Title */}
              <h1 className="flex flex-col items-center text-center mb-8 mt-4">Law Enforcement Sensitive <br />For Law Enforcement <br />Distribution Only</h1>

              {/* Introduction Paragraphs */}
              <p className="mb-4 leading-relaxed max-w-4xl mx-auto">
                The purpose of this report is to notify authorities of a possible criminal or terrorist threat discovered by the Clarion Intelligence Network.
              </p>
              <p className="mb-4 leading-relaxed max-w-4xl mx-auto">
                The Clarion Project is a 501c3 non-profit committed to reducing the threats of extremism. We believe it is our responsibility to bring attention to potential dangers and provide actionable insights to mitigate risks. For more information on our organization visit <a href="https://clarionproject.org/cin" target="_blank" rel="noopener noreferrer">https://clarionproject.org/cin</a>
              </p>

              {/* Contact Information */}
              <address className="mb-12 mt-8 max-w-4xl mx-auto not-italic leading-relaxed">
                For more information on this report, reference #{finalReportNumber}, contact us via email at <a href="mailto:intel@clarionproject.org">intel@clarionproject.org</a>.
              </address>
            </>
          </header>

          {/* Main Content */}
          <main aria-label="Threat report content">

          {/* Profile Section */}
          <section id="profile" aria-labelledby="profile-heading" className="mb-12">
            <h2 id="profile-heading" className="text-2xl font-bold uppercase mb-4">Profile</h2>
            
            {/* Profile Header Image - placeholder for future use */}
            {/* <figure>
              <img src="/path/to/profile-header.png" alt="Profile header image" />
             </figure> */}
            
            {/* Profile Section with three columns: two for fields, one for photo */}
            <table className="threat-report-table w-full border-collapse border border-gray-300 border-solid">
              <tbody>
                {/* Row 1: Real Name and Alias */}
                <tr className="border-b border-gray-300 border-solid">
                  <th scope="row" className="w-1/4 p-3 font-bold text-sm bg-gray-50 border-r border-gray-300 border-solid uppercase">Real Name</th>
                  <td className="w-1/4 p-3 bg-white">{finalProfile?.realName || ''}</td>
                  <th scope="row" className="w-1/4 p-3 font-bold text-sm bg-gray-50 border-r border-gray-300 border-solid uppercase">Alias</th>
                  <td className="w-1/4 p-3 bg-white">{finalProfile?.alias || ''}</td>
                  <td rowSpan={5} className="w-1/3 p-4 border-l border-gray-300 border-solid align-top">
                    <div className="flex items-start justify-center">
                      {finalProfile?.profilePhoto ? <figure className="m-0">
                          <img src={convertS3UrlToProxyUrl(finalProfile.profilePhoto)} alt={`Profile photo for ${finalProfile.alias || finalProfile.realName || 'user'}`} width={200} height={200} className="w-[200px] h-[200px] object-cover rounded-lg" loading="eager" />
                        </figure> : <div className="w-[200px] h-[200px] bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                          <UserIcon size={64} weight="regular" className="text-gray-400" />
                        </div>}
                    </div>
                  </td>
                </tr>

                {/* Row 2: DOB and Class. */}
                <tr className="border-b border-gray-300 border-solid">
                  <th scope="row" className="w-1/4 p-3 font-bold uppercase text-sm bg-gray-50 border-r border-gray-300 border-solid">Date of Birth</th>
                  <td className="w-1/4 p-3 bg-white">
                    {finalProfile?.dob ? <time dateTime={finalProfile.dob}>{finalProfile.dob}</time> : ''}
                  </td>
                  <th scope="row" className="w-1/4 p-3 font-bold uppercase text-sm bg-gray-50 border-r border-gray-300 border-solid">Classification</th>
                  <td className="w-1/4 p-3 bg-white">
                    {finalProfile?.classification ? <div className="inline-flex items-center px-2 py-1 rounded-md text-black" style={{
                        backgroundColor: classificationBoxColor
                      }}>
                        {finalProfile.classification}
                        <CaretDownIcon size={16} className="ml-1" />
                      </div> : ''}
                  </td>
                </tr>

                {/* Row 3: Address and Group */}
                <tr className="border-b border-gray-300 border-solid">
                  <th scope="row" className="w-1/4 p-3 font-bold uppercase text-sm bg-gray-50 border-r border-gray-300 border-solid">Address</th>
                  <td className="w-1/4 p-3 bg-white">{finalProfile?.address || ''}</td>
                  <th scope="row" className="w-1/4 p-3 font-bold text-sm bg-gray-50 border-r border-gray-300 border-solid uppercase">Group</th>
                  <td className="w-1/4 p-3 bg-white">
                    {finalProfile?.group ? <div className="inline-flex items-center px-2 py-1 rounded-md text-black" style={{
                        backgroundColor: groupBoxColor
                      }}>
                        {finalProfile.group}
                        <CaretDownIcon size={16} className="ml-1" />
                      </div> : ''}
                  </td>
                </tr>

                {/* Row 4: City, State and Ideology */}
                <tr className="border-b border-gray-300 border-solid">
                  <th scope="row" className="w-1/4 p-3 font-bold text-sm bg-gray-50 border-r border-gray-300 border-solid uppercase">City, State</th>
                  <td className="w-1/4 p-3 bg-white">{finalProfile?.cityState || ''}</td>
                  <th scope="row" className="w-1/4 p-3 font-bold text-sm bg-gray-50 border-r border-gray-300 border-solid uppercase">Ideology</th>
                  <td className="w-1/4 p-3 bg-white">{finalProfile?.ideology || ''}</td>
                </tr>

                {/* Row 5: Email and Phone */}
                <tr>
                  <th scope="row" className="w-1/4 p-3 font-bold text-sm bg-gray-50 border-r border-gray-300 border-solid uppercase">Email</th>
                  <td className="w-1/4 p-3 bg-white">
                    {finalProfile?.email && finalProfile.email !== 'N/A' ? <a href={`mailto:${finalProfile.email}`}>{finalProfile.email}</a> : 'N/A'}
                  </td>
                  <th scope="row" className="w-1/4 p-3 font-bold text-sm bg-gray-50 border-r border-gray-300 border-solid uppercase">Phone</th>
                  <td className="w-1/4 p-3 bg-white">
                    {finalProfile?.phone && finalProfile.phone !== 'N/A' ? <a href={`tel:${finalProfile.phone}`}>{finalProfile.phone}</a> : 'N/A'}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          {/* Contact Info & Social Media Accounts */}
          <section aria-labelledby="contacts-heading" className="mb-12">
            <h2 id="contacts-heading" className="text-xl font-bold uppercase mb-4">Contact Info & Social Media Accounts</h2>
            
            <table className="threat-report-table w-full border-collapse border border-gray-300 border-solid">
              <caption className="sr-only">Contact information and social media accounts</caption>
              <thead>
                <tr className="bg-gray-50">
                  <th scope="col" className="w-[80px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">Crypto</th>
                  <th scope="col" className="w-[140px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">Alias / Profile Names</th>
                  <th scope="col" className="w-[100px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">Platform</th>
                  <th scope="col" className="w-[120px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">ID / Account</th>
                  <th scope="col" className="p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">URL</th>
                  <th scope="col" className="w-[100px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">Date Created</th>
                  <th scope="col" className="w-[100px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">Public / Private</th>
                  <th scope="col" className="w-[80px] p-2 font-bold uppercase text-xs border-b border-gray-300 border-solid bg-gray-50">Active (Y/N)</th>
                </tr>
              </thead>
              <tbody>
                {finalContactInfo && finalContactInfo.length > 0 ? finalContactInfo.map((contact, index) => <tr key={index} className="border-b border-gray-300 border-solid">
                      <td className="w-[80px] p-2 border-r border-gray-300 border-solid bg-white">{contact.crypto || 'N/A'}</td>
                      <td className="w-[140px] p-2 border-r border-gray-300 border-solid bg-white">{contact.alias || ''}</td>
                      <td className="w-[100px] p-2 border-r border-gray-300 border-solid bg-white">{contact.platform || ''}</td>
                      <td className="w-[120px] p-2 border-r border-gray-300 border-solid bg-white">{contact.idAccount || ''}</td>
                      <td className="p-2 border-r border-gray-300 border-solid bg-white max-w-0">
                        {contact.url ? <a href={contact.url} target="_blank" rel="noopener noreferrer" className="block truncate">{contact.url}</a> : ''}
                      </td>
                      <td className="w-[100px] p-2 border-r border-gray-300 border-solid bg-white">
                        {contact.dateCreated ? <time dateTime={contact.dateCreated}>{contact.dateCreated}</time> : ''}
                      </td>
                      <td className="w-[100px] p-2 border-r border-gray-300 border-solid bg-white">
                        {contact.publicPrivate ? <data value={contact.publicPrivate.toLowerCase()}>{contact.publicPrivate}</data> : ''}
                      </td>
                      <td className="w-[80px] p-2 bg-white">
                        {contact.active ? <data value={contact.active.toLowerCase()}>{contact.active}</data> : ''}
                      </td>
                    </tr>) : <tr>
                    <td colSpan={8} className="p-4 text-center text-gray-500 bg-white">No contact information available</td>
                  </tr>}
              </tbody>
            </table>

          {/* Threat Assessment Section */}
          <article id="threat-assessment" aria-labelledby="threat-assessment-heading" itemScope itemType="https://schema.org/Report" className="mb-12">
            <h2 id="threat-assessment-heading" itemProp="name" className="text-xl font-bold uppercase mb-4">Threat Assessment</h2>
            
            <table className="threat-report-table w-full border-collapse border border-gray-300 border-solid">
              <tbody>
                {/* CLASSIFICATION */}
                <tr className="border-b border-gray-300 border-solid">
                  <th scope="row" className="w-1/3 p-3 font-bold uppercase text-sm bg-gray-50 border-r border-gray-300 border-solid">Classification</th>
                  <td className="w-2/3 p-3 bg-white">
                    {finalThreatAssessment?.classification ? <div className="inline-flex items-center px-2 py-1 rounded-md text-black" style={{
                          backgroundColor: classificationBoxColor
                        }}>
                        {finalThreatAssessment.classification}
                        <CaretDownIcon size={16} className="ml-1" />
                      </div> : ''}
                  </td>
                </tr>

                {/* GROUP AFFILIATION */}
                <tr className="border-b border-gray-300 border-solid">
                  <th scope="row" className="w-1/3 p-3 font-bold uppercase text-sm bg-gray-50 border-r border-gray-300 border-solid">Group Affiliation</th>
                  <td className="w-2/3 p-3 bg-white">
                    {finalThreatAssessment?.groupAffiliation ? <div className="inline-flex items-center px-2 py-1 rounded-md text-black" style={{
                          backgroundColor: groupBoxColor
                        }}>
                        {finalThreatAssessment.groupAffiliation}
                        <CaretDownIcon size={16} className="ml-1" />
                      </div> : ''}
                  </td>
                </tr>

                <tr>
                  <th scope="row" className="w-1/3 p-3 font-bold uppercase text-sm bg-gray-50 border-r border-gray-300 border-solid">Ideology</th>
                  <td className="w-2/3 p-3 bg-white">{finalThreatAssessment?.ideology || ''}</td>
                </tr>
              </tbody>
            </table>
            </article>

            {/* Narrative Threat Assessment Report */}
            {finalNarrativeThreatAssessmentMd && <article id="threat-report-assessment" aria-labelledby="narrative-assessment-heading" className="threat-report-assessment">
                <h2 id="narrative-assessment-heading" className="sr-only">Narrative Threat Assessment</h2>
                <ReactMarkdown className="text-black" remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {finalNarrativeThreatAssessmentMd}
                </ReactMarkdown>
              </article>}

            {/* Key Associates Subsection */}
            {finalKeyAssociates && finalKeyAssociates.length > 0 && <section aria-labelledby="key-associates-heading" className="mt-8">
                <h2 id="key-associates-heading" className="text-lg font-bold uppercase mb-4">Key Associates / Contacts</h2>
                
                <table className="threat-report-table w-full border-collapse border border-gray-300 border-solid">
                  <caption className="sr-only">Key associates and contacts</caption>
                  <thead>
                    <tr className="bg-gray-50">
                      <th scope="col" className="w-[140px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">Alias / Profile Names</th>
                      <th scope="col" className="w-[100px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">Platform</th>
                      <th scope="col" className="w-[120px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">ID / Account</th>
                      <th scope="col" className="p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">URL</th>
                      <th scope="col" className="w-[100px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">Date Created</th>
                      <th scope="col" className="w-[100px] p-2 font-bold uppercase text-xs border-r border-b border-gray-300 border-solid bg-gray-50">Public / Private</th>
                      <th scope="col" className="w-[80px] p-2 font-bold uppercase text-xs border-b border-gray-300 border-solid bg-gray-50">Active (Y/N)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finalKeyAssociates.map((associate, index) => <tr key={index} className="border-b border-gray-300 border-solid">
                        <td className="w-[140px] p-2 border-r border-gray-300 border-solid bg-white">{associate.alias || ''}</td>
                        <td className="w-[100px] p-2 border-r border-gray-300 border-solid bg-white">{associate.platform || ''}</td>
                        <td className="w-[120px] p-2 border-r border-gray-300 border-solid bg-white">{associate.idAccount || ''}</td>
                        <td className="p-2 border-r border-gray-300 border-solid bg-white max-w-0">
                          {associate.url ? <a href={associate.url} target="_blank" rel="noopener noreferrer" className="block truncate">{associate.url}</a> : ''}
                        </td>
                        <td className="w-[100px] p-2 border-r border-gray-300 border-solid bg-white">
                          {associate.dateCreated ? <time dateTime={associate.dateCreated}>{associate.dateCreated}</time> : ''}
                        </td>
                        <td className="w-[100px] p-2 border-r border-gray-300 border-solid bg-white">
                          {associate.publicPrivate ? <data value={associate.publicPrivate.toLowerCase()}>{associate.publicPrivate}</data> : ''}
                        </td>
                        <td className="w-[80px] p-2 bg-white">
                          {associate.active ? <data value={associate.active.toLowerCase()}>{associate.active}</data> : ''}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </section>}
          </section>

          {/* Actionable Threats Section */}
          {finalActionableThreats && finalActionableThreats.length > 0 && <section id="actionable-threats" aria-labelledby="actionable-threats-heading" className="mb-12">
              <h2 id="actionable-threats-heading" className="text-xl font-bold uppercase mb-4">Actionable Threats</h2>
              <ol className="actionable-threats list-decimal list-inside space-y-2">
                {finalActionableThreats.map((threat, index) => <li key={index} className="text-black">
                    {threat.date && <strong>{threat.date}</strong>}
                    {threat.date && threat.evidenceSummary && ': '}
                    {threat.evidenceSummary && <span>{threat.evidenceSummary}</span>}
                    {threat.link && <span> (<a href={threat.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline inline-block max-w-[600px] truncate">{threat.link}</a>)</span>}
                  </li>)}
              </ol>
            </section>}

          {/* Social Media Posts Section */}
          {finalSocialMediaPosts && finalSocialMediaPosts.length > 0 && <section id="social-posts" aria-labelledby="social-posts-heading" className="mb-12">
              <h2 id="social-posts-heading" className="text-xl font-bold uppercase mb-4">Social Media Posts</h2>
              <div className="flex flex-col gap-6">
                {finalSocialMediaPosts.map((post, index) => <table key={index} className="post w-full border-collapse border border-gray-300 border-solid">
                    <tbody>
                      {/* Header Section */}
                      <tr>
                        <td colSpan={2} className="bg-gray-200 px-4 py-2 border-b border-gray-300 border-solid">
                          {post.date && <time dateTime={post.date} className="font-bold uppercase">{post.date}</time>}
                        </td>
                      </tr>
                      {/* Content Area - Two Column Layout */}
                      <tr className="bg-white">
                        {/* Left Column - Text Content */}
                        <td className="w-1/2 p-4 border-r border-gray-300 border-solid align-top">
                          <div className="text-md leading-relaxed whitespace-pre-wrap">
                            {post.notes || ''}
                          </div>
                        </td>
                        {/* Right Column - Media */}
                        <td className="w-1/2 p-4 align-top">
                          {post.media && post.media.length > 0 && <div className="w-full max-w-[300px] flex flex-col gap-4">
                              {post.media.map((mediaItem, mediaIndex) => <div key={mediaIndex} className="w-full">
                                  {mediaItem.url && mediaItem.url.trim() !== '' && <>
                                      {mediaItem.type === 'image' ? <div className="w-full aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
                                          <img src={convertS3UrlToProxyUrl(mediaItem.url)} alt={`Social media image ${mediaIndex + 1} from post dated ${post.date || 'unknown date'}`} className="w-full h-full object-contain" />
                                        </div> : <div className="w-full aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
                                          <video src={convertS3UrlToProxyUrl(mediaItem.url)} controls aria-label={`Social media video ${mediaIndex + 1} from post dated ${post.date || 'unknown date'}`} className="w-full h-full object-contain">
                                            Your browser does not support the video tag.
                                          </video>
                                        </div>}
                                    </>}
                                </div>)}
                            </div>}
                        </td>
                      </tr>
                    </tbody>
                  </table>)}
              </div>
            </section>}


          </main>

          <footer className="mt-16 pt-4 border-t border-gray-300 text-sm">

            <section aria-label="Organization logos" className="flex flex-row items-start justify-center mb-8">
              <figure className="flex justify-center m-0">
                <img src="/clarion-tr-logos@2x.png" alt="Clarion Intelligence Network logos" className="scale-50" />
              </figure>
            </section>

            <h1 className="flex flex-col items-center text-center mb-8 mt-4">End of Report</h1>
              <p className="mb-4 leading-relaxed max-w-4xl mx-auto">
                The purpose of this report is to notify authorities of a possible criminal or terrorist threat.
              </p>
              <p className="mb-4 leading-relaxed max-w-4xl mx-auto">
                The Clarion Intelligence Network is a 501c3 non-profit committed to reducing the threats of extremism, and we believe it is our responsibility to bring attention to potential dangers and provide actionable insights to mitigate risks.
              </p>
              <p className="not-italic leading-relaxed max-w-4xl mx-auto">
                For more information on this report or our organization, please visit <a href="http://clarionproject.org" target="_blank" rel="noopener noreferrer"><strong>http://clarionproject.org</strong></a> or contact us via email at <a href="mailto:intel@clarionproject.org"><strong>intel@clarionproject.org</strong></a>.
              </p>
              <div className="flex flex-row justify-between items-center mt-4">
                <div>Clarion Intelligence Network, {finalReportNumber}</div>
              </div>
          </footer>

        </div>
      </div>}
    </div>
    </>;
};
export default ThreatReport;